import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import vetImage from "assets/images/vet.jpg";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { readMember } from "services/staff.service";

type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  contractId: string;
};

const UpdateStaffForm = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const userId = id ? Number(id) : NaN;

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      title: "mr",
      firstName: "",
      lastName: "",
      email: "",
      contractId: "",
    },
  });

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id || !Number.isFinite(userId) || userId <= 0 || orgId <= 0) return;
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readMember(token, orgId, userId);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        reset({
          contractId: String(data.userId),
          email: data.emailAddress ?? "",
          firstName: data.contactFirstName ?? "",
          lastName: data.contactLastName ?? "",
          title: "mr",
        });
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, userId, orgId, getAccessTokenSilently, reset]);

  const onSubmit: SubmitHandler<FormValues> = async () => {
    setNotice(
      "Member profile updates are not exposed on this route; details are loaded read-only from Read_Member."
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-10">
      {loadError && (
        <div className="mb-4 text-sm text-red-500">{loadError}</div>
      )}
      {notice && (
        <div className="mb-4 text-sm text-amber-700">{notice}</div>
      )}
      <div className="flex flex-col gap-6 sm:flex-row sm:gap-x-10">
        <img
          src={vetImage}
          alt=""
          className="border border-black rounded-full aspect-square h-44 shrink-0"
        />
        <div className="grow">
          <div className="flex flex-col gap-6 mb-8 sm:flex-row sm:gap-x-10">
            <Input
              {...register("contractId")}
              label="User Id"
              placeholder="—"
              className=""
              disabled
            />

            <Input
              {...register("email")}
              label="Email Address"
              placeholder="Enter email address"
              className=""
              disabled
            />
          </div>

          <div className="flex flex-col gap-6 mb-10 sm:flex-row sm:gap-x-10">
            <Select
              {...register("title")}
              label="Title"
              placeholder="Enter"
              className="w-24"
              options={[
                { title: "MR", value: "mr" },
                { title: "MRS", value: "mrs" },
              ]}
              disabled
            />

            <Input
              {...register("firstName")}
              label="First Name"
              placeholder="Enter first name"
              disabled
            />

            <Input
              {...register("lastName")}
              label="Last Name"
              placeholder="Enter last name"
              disabled
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button color="Primary" type="submit">
          <span>Save (read-only)</span>
        </Button>
      </div>
    </form>
  );
};

export default UpdateStaffForm;
